<template>
  <AppNavigation mode="topbar"/>
  <div class="blog-container" v-if="post">
    <article class="blog-content">
      <img v-if="post.heroImage" :src="post.heroImage" :alt="post.title" lang="en" class="blog-hero" />
      <!-- Article titles and bodies are authored in English and stay English by
           decision, so they carry lang="en" while the document around them
           follows the reader's locale. Without it a screen reader announces
           English prose with (say) a Japanese voice. -->
      <h1 lang="en">{{ post.title }}</h1>
      <p class="blog-date">{{ formattedDate }}</p>
      <div v-html="post.content" lang="en"></div>

      <!-- Share Links -->
      <div class="share-section">
        <span class="share-label">{{ $t("blog.shareThisArticle") }}</span>
        <div class="share-links">
          <a :href="twitterShareUrl" target="_blank" rel="noopener" class="share-btn share-twitter" :aria-label="$t('blog.shareOnX')">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          </a>
          <a :href="facebookShareUrl" target="_blank" rel="noopener" class="share-btn share-facebook" :aria-label="$t('blog.shareOnFacebook')">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          </a>
          <a :href="linkedinShareUrl" target="_blank" rel="noopener" class="share-btn share-linkedin" :aria-label="$t('blog.shareOnLinkedIn')">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
          </a>
          <button @click="copyLink" class="share-btn share-copy" :aria-label="$t('blog.copyLink')">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
            <span v-if="copied" class="copied-text">{{ $t("blog.copied") }}</span>
          </button>
        </div>
      </div>

      <!-- Related Posts -->
      <div class="related-section" v-if="relatedPosts.length">
        <h2>{{ $t("blog.relatedPosts") }}</h2>
        <div class="related-grid">
          <router-link
            v-for="related in relatedPosts"
            :key="related.slug"
            :to="'/blog/' + related.slug"
            class="related-card"
          >
            <div class="related-card-image" v-if="related.heroImage">
              <img :src="related.heroImage" :alt="related.title" lang="en" />
            </div>
            <div class="related-card-body">
              <h3 lang="en">{{ related.title }}</h3>
              <p class="related-card-date">{{ formatDate(related.date) }}</p>
            </div>
          </router-link>
        </div>
      </div>
    </article>
  </div>
  <div class="blog-container" v-else>
    <div class="blog-content">
      <h1>{{ $t("blog.postNotFound") }}</h1>
      <p>{{ $t("blog.postNotFoundBody") }} <router-link to="/blog">{{ $t("blog.browseAllPosts") }}</router-link>.</p>
    </div>
  </div>
  <FooterSection/>
</template>

<script>
import AppNavigation from "@/components/AppNavigation.vue";
import FooterSection from "@/components/FooterSection.vue";
import { useHead } from "@unhead/vue";
import { publicPageHead } from "@/scripts/publicHead.js";
import { siteUrl } from "@/data/siteOrigin.js";
import { authUtils } from "@/scripts/authUtils.js";
import { blogPostsBySlug, blogPosts } from "@/data/blog/index.js";
import { formatLongDate } from "@/scripts/dateUtils.js";

export default {
  name: "BlogPostView",
  components: { AppNavigation, FooterSection },
  data() {
    return { copied: false };
  },
  computed: {
    post() {
      return blogPostsBySlug[this.$route.params.slug] || null;
    },
    formattedDate() {
      if (!this.post) return "";
      return this.formatDate(this.post.date);
    },
    postPath() {
      return `/blog/${this.post?.slug || ""}`;
    },
    postUrl() {
      // Through siteUrl rather than a hardcoded origin: this URL is both the
      // canonical/og:url of the page and the link the share buttons below hand
      // to Twitter, Facebook and LinkedIn, so on a QA build it has to name the
      // QA host or a preview can never be validated before a prod release.
      return siteUrl(this.postPath);
    },
    twitterShareUrl() {
      return `https://twitter.com/intent/tweet?url=${encodeURIComponent(this.postUrl)}&text=${encodeURIComponent(this.post?.title || "")}`;
    },
    facebookShareUrl() {
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.postUrl)}`;
    },
    linkedinShareUrl() {
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(this.postUrl)}`;
    },
    relatedPosts() {
      if (!this.post) return [];
      return blogPosts
        .filter(p => p.slug !== this.post.slug)
        .slice(0, 3);
    },
  },
  watch: {
    "$route.params.slug"() {
      this.updateHead();
      window.scrollTo(0, 0);
    },
  },
  methods: {
    updateHead() {
      if (this.post) {
        // Plain strings rather than computed refs, unlike every other public
        // view: the article title and description are authored English and
        // stay English by decision, so there is no locale bundle to wait for.
        //
        // No htmlAttrs.lang here either: <html lang> is owned by the locale
        // watcher in src/locales/index.js and describes the interface the
        // reader is using. The English article declares itself with lang="en"
        // on the elements that actually hold English prose.
        useHead(
          publicPageHead({
            title: this.post.title,
            description: this.post.description,
            path: this.postPath,
            // The post's own hero, which the registry also declares as this
            // route's og:image. Root-relative in the data; publicPageHead
            // absolutizes it, because Twitter/X rejects a relative one.
            image: this.post.heroImage,
            type: "article",
          }),
        );
      }
    },
    formatDate(dateStr) {
      // "T00:00:00" pins the day to local midnight; parsing a bare "YYYY-MM-DD"
      // is UTC by spec and lands on the previous day west of Greenwich.
      return formatLongDate(dateStr + "T00:00:00", this.$i18n.locale);
    },
    async copyLink() {
      await navigator.clipboard.writeText(this.postUrl);
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 2000);
    },
  },
  mounted() {
    authUtils.waitForAuthInitialization();
    this.updateHead();
  },
};
</script>

<style scoped>
.blog-container {
  display: flex;
  justify-content: center;
  padding: 100px 1rem 4rem;
}

.blog-content {
  max-width: 680px;
  width: 100%;
  color: var(--al-gray-700);
  line-height: 1.7;
}

.blog-hero {
  width: 100%;
  max-height: 400px;
  object-fit: cover;
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.blog-content h1 {
  font-size: 2rem;
  margin-bottom: 0.25rem;
  color: var(--al-ink-900);
}

.blog-date {
  color: var(--al-gray);
  font-size: 0.9rem;
  margin-bottom: 2rem;
}

.blog-content :deep(h2) {
  font-size: 1.3rem;
  margin-top: 2rem;
  margin-bottom: 0.5rem;
}

.blog-content :deep(h3) {
  font-size: 1.1rem;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

.blog-content :deep(p),
.blog-content :deep(li) {
  font-size: 0.95rem;
}

.blog-content :deep(ul),
.blog-content :deep(ol) {
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

.blog-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 1rem 0;
}

.blog-content :deep(a) {
  color: var(--al-primary-hover);
}

/* Table styling for v-html content */
.blog-content :deep(table) {
  width: 100%;
  /* border-radius does not render on collapsed tables; separate + zero
     spacing keeps the same layout with working rounded corners. */
  border-collapse: separate;
  border-spacing: 0;
  margin: 1.5rem 0;
  border: 1px solid var(--al-gray-300);
  border-radius: 8px;
  overflow: hidden;
}

.blog-content :deep(td),
.blog-content :deep(th) {
  padding: 12px 16px;
  border: 1px solid var(--al-gray-300);
  vertical-align: top;
}

.blog-content :deep(tr:nth-child(even)) {
  background-color: var(--al-bg-light);
}

.blog-content :deep(td h3) {
  margin-top: 0;
  font-size: 1rem;
}

/* Share section */
.share-section {
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--al-gray-300);
}

.share-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--al-gray-700);
  display: block;
  margin-bottom: 0.75rem;
}

.share-links {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.share-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--al-gray-300);
  background: var(--al-white);
  color: var(--al-gray-700);
  text-decoration: none;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
}

.share-btn:hover {
  background-color: var(--al-surface-muted);
  color: var(--al-ink-900);
}

/* Brand hover colors are intentional: these represent the third-party
   platforms being shared to, not app UI accents. */
.share-twitter:hover { color: #000; }
.share-facebook:hover { color: #1877f2; }
.share-linkedin:hover { color: #0a66c2; }

.copied-text {
  font-size: 0.8rem;
  color: var(--al-success);
}

/* Related posts */
.related-section {
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--al-gray-300);
}

.related-section h2 {
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: var(--al-ink-900);
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.related-card {
  border: 1px solid var(--al-gray-300);
  border-radius: 10px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.2s ease;
}

.related-card:hover {
  box-shadow: var(--al-shadow-sm);
}

.related-card-image {
  width: 100%;
  height: 120px;
  overflow: hidden;
}

.related-card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.related-card-body {
  padding: 0.75rem;
}

.related-card-body h3 {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--al-ink-900);
  line-height: 1.3;
  margin: 0 0 0.35rem;
}

.related-card-date {
  font-size: 0.75rem;
  color: var(--al-gray);
  margin: 0;
}

@media (max-width: 767px) {
  .blog-container {
    padding-top: calc(var(--al-topbar-height) + 24px);
  }

  .related-grid {
    grid-template-columns: 1fr;
  }
}
</style>
